import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { AlertTriangle, Boxes, Recycle, Scale, TrendingDown } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Card from '../../components/ui/Card';
import KpiTile from '../../components/ui/KpiTile';
import MachineFilters from '../../components/ui/MachineFilters';
import PageHeader from '../../components/ui/PageHeader';
import { ROUTES } from '../../config/routes';
import { useMachineFilters } from '../../hooks/useMachineFilters';
import { alarmsApi } from '../../services/alarmsApi';
import { insightsApi, type ComposterInsightRow } from '../../services/insightsApi';

const formatKg = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg`;
const formatNum = (n: number) => n.toLocaleString();

const DashboardView = () => {
  const f = useMachineFilters({ machineType: 'composter' });

  const insightsQuery = useQuery({
    queryKey: ['insights', 'composter', f.selectedMachineId, f.from, f.to],
    enabled: Boolean(f.selectedMachineId),
    queryFn: async () =>
      (await insightsApi.composter(f.selectedMachineId as string, f.from, f.to)).data,
  });

  const alarmsQuery = useQuery({
    queryKey: ['alarms', f.selectedMachineId],
    enabled: Boolean(f.selectedMachineId),
    queryFn: async () => (await alarmsApi.list(f.selectedMachineId as string)).data,
  });

  const totals = insightsQuery.data?.totals;
  const rows = insightsQuery.data?.rows ?? [];
  const recentAlarms = (alarmsQuery.data ?? []).slice(0, 5);

  return (
    <>
      <PageHeader
        title="Composter Dashboard"
        subtitle="Daily totals across your subscribed machines."
        actions={
          <MachineFilters
            machines={f.machines}
            selectedMachineId={f.selectedMachineId}
            onMachineChange={f.setMachineId}
            from={f.from}
            to={f.to}
            onFromChange={f.setFrom}
            onToChange={f.setTo}
            loading={f.machinesLoading}
          />
        }
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          label="Totes loaded"
          value={totals ? formatNum(totals.totesLoaded) : '—'}
          Icon={Boxes}
          loading={insightsQuery.isFetching}
        />
        <KpiTile
          label="Kgs loaded"
          value={totals ? formatKg(totals.kgsLoaded) : '—'}
          Icon={Scale}
          loading={insightsQuery.isFetching}
        />
        <KpiTile
          label="Kgs offloaded"
          value={totals ? formatKg(totals.kgsOffloaded) : '—'}
          Icon={Recycle}
          loading={insightsQuery.isFetching}
        />
        <KpiTile
          label="Weight reduced"
          value={totals ? formatKg(totals.weightReduced) : '—'}
          Icon={TrendingDown}
          loading={insightsQuery.isFetching}
        />
      </section>

      <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card
            title="Daily kgs (loaded vs. offloaded)"
            subtitle={`${rows.length} day${rows.length === 1 ? '' : 's'} in window`}
          >
            <ReactECharts
              option={mainChartOption(rows)}
              style={{ height: 320 }}
              notMerge
              lazyUpdate
            />
          </Card>
        </div>

        <Card
          title="Recent alarms"
          subtitle="Last 5 events for this machine"
          action={
            <Link
              to={ROUTES.ALARMS}
              className="text-xs font-medium text-tfs-primary hover:underline"
            >
              View all →
            </Link>
          }
        >
          {recentAlarms.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-400">No alarms in window.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {recentAlarms.map((a) => (
                <li
                  key={a.uid}
                  className="flex items-start gap-3 rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2"
                >
                  <span
                    className={`mt-0.5 inline-flex shrink-0 size-7 items-center justify-center rounded-full ${
                      a.severity === 'critical'
                        ? 'bg-red-100 text-red-600'
                        : a.severity === 'fault'
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    <AlertTriangle className="size-3.5" aria-hidden />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="text-sm font-medium text-slate-800">{a.label}</span>
                    <span className="text-[11px] text-slate-500">
                      {a.timestampUtc
                        ? new Date(a.timestampUtc).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SensorMiniCard label="Pile temperature" unit="°C" rows={rows} field="pileTemp" tone="emerald" />
        <SensorMiniCard label="Heat pad temp." unit="°C" rows={rows} field="heatPadTemp" tone="amber" />
        <SensorMiniCard label="Avg moisture" unit="%" rows={rows} field="avgMoisture" tone="sky" />
        <SensorMiniCard label="Pile level" unit="cm" rows={rows} field="pileLevel" tone="violet" />
      </section>
    </>
  );
};

const mainChartOption = (rows: ComposterInsightRow[]) => ({
  grid: { left: 50, right: 30, top: 30, bottom: 40 },
  tooltip: { trigger: 'axis' as const },
  legend: { data: ['kgs loaded', 'kgs offloaded'], top: 0, textStyle: { color: '#475569' } },
  xAxis: {
    type: 'category' as const,
    data: rows.map((r) => r.ts.slice(5)),
    axisLabel: { color: '#64748b' },
    axisLine: { lineStyle: { color: '#e2e8f0' } },
  },
  yAxis: {
    type: 'value' as const,
    axisLabel: { color: '#64748b', formatter: (v: number) => `${v}kg` },
    splitLine: { lineStyle: { color: '#e2e8f0' } },
  },
  series: [
    {
      name: 'kgs loaded',
      type: 'line' as const,
      smooth: true,
      showSymbol: false,
      data: rows.map((r) => r.kgsLoaded),
      itemStyle: { color: '#2d5f3f' },
      areaStyle: { color: 'rgba(45, 95, 63, 0.12)' },
    },
    {
      name: 'kgs offloaded',
      type: 'line' as const,
      smooth: true,
      showSymbol: false,
      data: rows.map((r) => r.kgsOffloaded),
      itemStyle: { color: '#9bc53d' },
    },
  ],
});

const TONE_COLORS = {
  emerald: '#10b981',
  amber: '#f59e0b',
  sky: '#0ea5e9',
  violet: '#8b5cf6',
} as const;

interface SensorMiniProps {
  label: string;
  unit: string;
  rows: ComposterInsightRow[];
  field: 'pileTemp' | 'heatPadTemp' | 'avgMoisture' | 'pileLevel';
  tone: keyof typeof TONE_COLORS;
}

const SensorMiniCard = ({ label, unit, rows, field, tone }: SensorMiniProps) => {
  const series = rows.map((r) => r[field]).filter((v): v is number => v != null);
  const latest = series[series.length - 1] ?? null;
  const avg = series.length ? series.reduce((a, b) => a + b, 0) / series.length : null;

  const option = useMemo(
    () => ({
      grid: { left: 0, right: 0, top: 0, bottom: 0 },
      xAxis: { type: 'category' as const, show: false, data: rows.map((r) => r.ts) },
      yAxis: { type: 'value' as const, show: false, scale: true },
      series: [
        {
          type: 'line' as const,
          smooth: true,
          showSymbol: false,
          data: rows.map((r) => r[field] ?? null),
          itemStyle: { color: TONE_COLORS[tone] },
          areaStyle: { color: `${TONE_COLORS[tone]}22` },
        },
      ],
      tooltip: { show: false },
    }),
    [rows, field, tone],
  );

  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tracking-tight text-slate-900">
          {latest != null ? latest.toFixed(0) : '—'}
        </span>
        <span className="text-xs text-slate-500">{unit}</span>
      </div>
      <p className="mt-0.5 text-[11px] text-slate-500">
        Avg {avg != null ? avg.toFixed(1) : '—'}
        {unit}
      </p>
      <div className="mt-3 h-12">
        <ReactECharts option={option} style={{ height: 48 }} notMerge lazyUpdate />
      </div>
    </div>
  );
};

export default DashboardView;
